import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../classes/util';
import { ESRI } from '../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../inputConverter';
import { IAuthenticationService } from '../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { LayerService } from '../../services/layer.service';
import { PrintService } from '../../services/print.service';
import { IDefaultPrintTemplate, IPrintLayout, LogLevel, MapCoreLayer } from '../../types';
import { BaseComponent } from '../base.component';

let Graphic: __esri.GraphicConstructor;
let SimpleLineSymbol: __esri.SimpleLineSymbolConstructor;

const wait = 50;
const extentTrimAmount = 0.95;
const initilizeDebounceTime = 50;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-print',
    styleUrls: ['../sharedStyles/base.scss', './print.component.scss'],
    templateUrl: './print.component.html'
})
export class PrintComponent extends BaseComponent implements OnInit {
    _active: any;
    @Input()
    @InputConverter(BooleanConverter)
    get active() {
        return this._active;
    }
    set active(value: any) {
        if (value) { // detail is value prop sent by angular event
            this._active = value;
            if (!this.loadingOutputUrl && !this.outputLoaded) {
                this.onActivate();
            }
            this.setupHandleHiddenLayer();
        } else {
            this.reset();
            this._active = false;
            this.teardownHandleHiddenLayer();
        }
    }

    @Output() readonly ondone: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readonly onoutputloaded: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        _vcr: ViewContainerRef,
        @Inject('authenticationService') public authService: IAuthenticationService,
        private readonly layerService: LayerService,
        private readonly printService: PrintService,
        private readonly cdr: ChangeDetectorRef,
        @Inject('LoggingService') public LoggingService: ILoggingService
    ) {
        super(_vcr, true);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onoutputloaded', this.onoutputloaded);
    }

    async ngOnInit() {
        this.drawExtent = util.debounce(this.drawExtentWork.bind(this), initilizeDebounceTime);
        try {
            [Graphic, SimpleLineSymbol] = await Promise.all([ESRI.Graphic, ESRI.SimpleLine]);
            this.mapView = await this.setupMapViewRef(this.mapView);
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    loadingOutputUrl = false;
    outputLoaded = false;
    outputUrl: string;
    errorMessage;

    layers: MapCoreLayer[] = [];

    /**
     * The selected print layout
     */
    public get selectedLayout() {
        return this._selectedLayout;
    }
    public set selectedLayout(value) {
        this._selectedLayout = value;
        this.drawExtent();
    }
    private _selectedLayout: IPrintLayout;

    @Input() message: string;

    @Input() mapView: NonNullable<__esri.MapView>;

    /**
     * The list of all available layouts for the specified print service
     */
    public layouts: IPrintLayout[];

    /**
     * The title that should given to the exported map
     */
    public mapTitle: string;
    @Input() defaultMapTitle = 'My Map';

    /**
     * If enabled, the legend will be included in the exported map.
     * If disabled, the legend will be omitted from the exported map.
     */
    @Input() public includeLegend = true;

    /**
     * Indicates whether the advanced options panel is expaned or collapsed.
     */
    isAdvancedOptionsExpanded = false;

    /**
     * The name of the person who authored the exported map.
     * Should default to the current user's name.
     */
    public author: string;

    /**
     * The Map Selected criteria to the exported map
     */
    @Input() mapSelect: string;

    /**
     * Copyright text to display in the exported map.
     */
    @Input() public copyright = 'PennDOT';

    /**
     * The units the scalebar use in the exported map.
     */
    public scalebarUnits: 'Miles' | 'Kilometers' | 'Meters' | 'Feet' = 'Miles';

    /**
     * The output format to use in the exported map.
     */
    public format: 'pdf' | 'png' | 'jpg' = 'pdf';
    public get formatDisplay() {
        let retVal = '';
        switch (this.format) {
            case 'pdf':
                retVal = 'PDF';
                break;
            case 'png':
                retVal = 'PNG';
                break;
            case 'jpg':
                retVal = 'JPG';
                break;
            default:
                retVal = 'Output';
                break;
        }
        return retVal;
    }

    /**
     * Scale drawing layers to extent.
     */
    public scaleDrawingLayers = false;

    /**
     * When checked, the user will receive an email when the export has completed.
     */
    public isReceiveEmailChecked = false;

    /**
     * Basemap setting
     */
    private readonly useCurrentBasemap: boolean = true;

    /**
     * A promise that resolves The layer used to draw the extent on the map
     */
    private extentLayer: __esri.GraphicsLayer;

    /**
     * The sketcht tool used to edit the extent on the map
     */
    private sketch: __esri.Sketch;

    /**
     * The unique id for the extent graphics layer used by this component.
     */
    private readonly extentLayerId = `mc-print-extent-${this.componentId}`;

    /**
     * Currently Signed in user
     */
    private user;

    private scaleWatcher: __esri.WatchHandle;

    /**
     * Warning message
     */
    warning: string;

    /**
     * Legend table
     */
    // private legendTable = [];

    /**
     * Checks if any layers have routing details
     */
    public hasRoutingDetails = false;

    async onActivate() {
        await Promise.all([this.getCurrentUser(), this.loadLayouts()]);
        this.scaleWatcher = this.mapView.watch('stationary', (stationary: boolean) => {
            if (stationary) {
                // Map stopped moving, check if print extent is still contained on the screen
                if (!this.extentLayer || !this.extentLayer.graphics.length || !this.mapView.extent.contains(this.extentLayer.graphics.getItemAt(0).geometry.extent)) {
                    if (this.sketch) {
                        this.sketch.complete();
                    }
                    this.drawExtent();
                }
            }
        });
        this.layers = this.mapView.map.layers.toArray().reverse() as MapCoreLayer[];
        let anyHaveRoutingDetails = false;
        for (const layer of this.layers) {
            if (layer.mapcoreProps) {
                layer.mapcoreProps.showInPrintLegend = true;

                if (layer.mapcoreProps.routingDetail) {
                    layer.mapcoreProps.printRoutingDetail = true;
                    anyHaveRoutingDetails = true;
                }
            }
        }
        this.hasRoutingDetails = anyHaveRoutingDetails;

        this.cdr.detectChanges();
    }

    private async getCurrentUser() {
        this.authService.user.subscribe(user => {
            if (user) {
                this.user = user;
                this.author = user.emailAddress;
            }
        });
    }

    async loadLayouts() {
        this.layouts = await this.printService.loadLayouts();
        if (!this.selectedLayout) {
            this.selectedLayout = this.layouts[0];
        } else {
            this.selectedLayout = this.layouts.find(x => x.id === this.selectedLayout.id);
            this.drawExtent();
        }
    }

    private drawExtent: () => void;
    /**
     * Draw the current user's extent on the map, bounded by their selected template.
     * Safe to call many times, as this redraws every time.
     */
    private async drawExtentWork() {
        if (!this.selectedLayout) {
            this.loadLayouts();
        }

        if (!this.extentLayer) {
            await this.createExtentLayer();
        }

        if (this.mapView && this.extentLayer && this.selectedLayout) {
            this.sketch.view = this.mapView;
            this.sketch.viewModel.defaultUpdateOptions.enableScaling = true;
            this.sketch.viewModel.defaultUpdateOptions.preserveAspectRatio = true;
            this.sketch.viewModel.defaultUpdateOptions.enableRotation = false;
            this.sketch.viewModel.defaultUpdateOptions.multipleSelectionEnabled = false;

            //clear any graphics that are on the map
            this.extentLayer.removeAll();

            //ensure the map is fully ready before proceeding
            await this.mapView.when();
            await util.sleep(wait);

            //adjust the user's extent to fit the template aspect ratio
            const extent = this.getExtentWithCorrectAspectRatio(
                this.selectedLayout.widthInches,
                this.selectedLayout.heightInches,
                this.mapView.extent
            );

            const polygon = await this.getPolygonFromExtent(extent);
            const graphic = new Graphic({
                geometry: polygon,
                symbol: new SimpleLineSymbol({
                    color: [255, 0, 0, 0.6],
                    width: 6
                })
            });
            //add the extent polygon to the map
            this.extentLayer.add(graphic);
            this.extentLayer.visible = true;
        }
    }

    private async getPolygonFromExtent(extent: __esri.Extent): Promise<__esri.Polygon> {
        const Polygon = await ESRI.Polygon;

        const polygon = new Polygon({
            spatialReference: extent.spatialReference
        });
        polygon.addRing([
            [extent.xmin, extent.ymin],
            [extent.xmin, extent.ymax],
            [extent.xmax, extent.ymax],
            [extent.xmax, extent.ymin],
            [extent.xmin, extent.ymin]
        ]);
        return polygon;
    }

    /**
     * Given the the width and height of a template, resize the extent so that it is the same aspect ratio as that template
     */
    private getExtentWithCorrectAspectRatio(templateWidth: number, templateHeight: number, mapExtent: __esri.Extent) {
        const extent = mapExtent.clone();
        const screenWidth = extent.xmax - extent.xmin;
        const screenHeight = extent.ymax - extent.ymin;

        //find the amount to change x extent by to match ratio
        const amountToChangeX = ((screenHeight * templateWidth) / templateHeight) - screenWidth;
        //find the amount to change y extent by to match ratio. j = (yw/h) - x
        const amountToChangeY = ((screenWidth * templateHeight) / templateWidth) - screenHeight;

        const xWillChange = amountToChangeX > 0 ? false : true;

        if (xWillChange) {
            const half = amountToChangeX / 2;
            extent.xmin -= half;
            extent.xmax += half;
        } else {
            const half = amountToChangeY / 2;
            extent.ymin -= half;
            extent.ymax += half;
        }
        return extent.expand(extentTrimAmount);
    }

    private async createExtentLayer() {
        if (!this.extentLayer) {
            this.extentLayer = await this.layerService.addGraphicsLayer({
                id: this.extentLayerId,
                hideInLegend: true,
                visible: false
            });
            (<MapCoreLayer>this.extentLayer).mapcoreProps = { hideInLegend: true };
            const Sketch = await ESRI.Sketch;
            this.sketch = new Sketch({
                layer: this.extentLayer,
                defaultUpdateOptions: {
                    enableRotation: false,
                    multipleSelectionEnabled: false,
                    preserveAspectRatio: true,
                    toggleToolOnClick: false
                }
            });
            this.sketch.watch('state', (state: 'ready' | 'disabled' | 'active') => {
                if (state === 'active') {
                    (<any>this.sketch.viewModel).activeComponent.layer.mapcoreProps = { hideInLegend: true };
                    this.layerService.refreshLayers();
                }
            });
        }
    }

    public getEmailParams() {
        const params = {
            ToAddresses: [this.user.emailAddress],
            FromAddress: this.user.emailAddress,
            FromName: this.user.emailAddress,
            Subject: `${this.mapTitle} is complete and ready to print`,
            Body: `The map ${this.mapTitle} created by ${this.user.emailAddress} is complete and ready to be printed. \nThe following link will expire between 15 and 30 minutes from now: {0} \n \nWithin 30 minutes the map will be moved to this location for the next 7 days: {1} \n \nIf you will need access the map after this time expires, please save it locally.`,
            Cc: null,
            Bcc: null,
            IsHtml: false
        };

        return params;
    }

    async startPrint() {
        this._restarted = false;
        this.loadingOutputUrl = true;
        if (!this.mapTitle) {
            this.mapTitle = this.defaultMapTitle;
        }
        if (!this.mapSelect) {
            this.mapSelect = '';
        }
        const template: IDefaultPrintTemplate = {
            selectedLayout: this.selectedLayout,
            label: '',
            format: this.format as any,
            layoutOptions: {
                titleText: this.mapTitle,
                authorText: this.author,
                copyrightText: this.copyright,
                scalebarUnit: this.scalebarUnits,
                customTextElements: [{ selectText: this.mapSelect }],
                legendLayers: null
            },
            exportOptions: {
                dpi: 300
            }
        };

        try {
            this.sketch.complete();
            const extent = this.extentLayer.graphics.getItemAt(0).geometry.extent.clone();
            this.removeExtentLayerFromMap();
            this.outputUrl = await this.printService.printMap(
                this.mapView,
                template,
                extent,
                this.includeLegend,
                this.user && this.isReceiveEmailChecked ? this.getEmailParams() : null,
                this.scaleDrawingLayers, //TODO: Scale drawing layers is an app config option in 1.x. Set to false by default, should we enable a choice?
                this.useCurrentBasemap,
                this.hasRoutingDetails
            );
            if (!this._restarted) {
                this.loadingOutputUrl = false;
                this.outputLoaded = true;
                this.onoutputloaded.emit(true);
                this.LoggingService.Log(`${this.formatDisplay} ready to download.`, null, LogLevel.Info);
            }
        } catch (error) {
            const message = `There was an error loading the ${this.formatDisplay}, please try again or contact an administrator for help.`;
            if (!this._restarted) {
                this.loadingOutputUrl = false;
                this.errorMessage = message;
                this.onoutputloaded.emit(false);
                //TODO: Error reporting
            }
            this.LoggingService.Log(message, error, LogLevel.Error, !this._restarted);
        } finally {
            this._restarted = false;
        }
        this.cdr.detectChanges();
    }

    openOutput() {
        window.open(this.outputUrl, '_blank');
        this.ondone.emit(true);
        this.restart();
    }

    private _restarted = false;
    restart() {
        this._restarted = true;
        this.reset();
        this.onActivate();
    }

    removeExtentLayerFromMap() {
        this.mapView.map.remove(this.extentLayer);
        this.extentLayer.removeAll();
        this.extentLayer = null;
    }

    reset() {
        if (this.extentLayer) {
            this.removeExtentLayerFromMap();
        }
        this.loadingOutputUrl = false;
        this.outputUrl = null;
        this.outputLoaded = false;
        this.mapTitle = null;
        this.errorMessage = '';
        if (this.scaleWatcher) {
            this.scaleWatcher.remove();
        }
    }

    layoutTrack(item: IPrintLayout) {
        return item.id;
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('drawLineToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('drawLineToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }
}
